import { isPlatformBrowser } from '@angular/common';
import { HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject, PLATFORM_ID } from '@angular/core';
import { AuthService, BASE_API_URL, IAuthTokenUser, SESSION_EXPIRED_HANDLER } from '@cnt-workspace/data-access';
import { DateTime } from 'luxon';
import { Observable, of, race, throwError } from 'rxjs';
import { first, map, switchMap, timeout } from 'rxjs/operators';

/** What a save shows when the session ran out; the expired handler asks the admin to sign in again. */
export const SESSION_EXPIRED_MESSAGE = 'Your session expired: sign in again, then save.';
const OFFLINE_MESSAGE = "You're offline: nothing was saved.";
/** A renewal that hasn't answered by then is treated as failed, so no save waits forever. */
const RENEW_WAIT_MS = 20000;

// Copy of cnt-landing's apps/cnt-landing/src/app/interceptors/jwt.interceptor.ts
// (that one lives in the app folder, not a lib, so it can't be imported here).
// Attaches the Cognito id token from AuthService to calls to our own API,
// renewing first if it's expired or within 30s of expiring. Unlike that copy,
// a renewal that fails, times out or leaves no session ends the request with
// an error instead of leaving it waiting.
const attachAuthHeader = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
  auth: AuthService,
  expired: (() => void) | null,
): Observable<HttpEvent<unknown>> => {
  const ended = () => {
    expired?.();
    return throwError(() => new Error(SESSION_EXPIRED_MESSAGE));
  };
  return auth.authSession$.pipe(
    first(),
    switchMap((currentUser: IAuthTokenUser | undefined) => {
      if (currentUser && (!currentUser.authExpires || currentUser.authExpires < DateTime.now().toMillis() + 30000)) {
        setTimeout(() => auth.triggerRenew());
        return race(
          auth.renewResult$.pipe(first()),
          auth.renewFailed$.pipe(
            first(),
            map(() => 'failed' as const),
          ),
        ).pipe(
          timeout({ first: RENEW_WAIT_MS, with: () => of('failed' as const) }),
          switchMap((result) => {
            if (result === 'deferred') return throwError(() => new Error(OFFLINE_MESSAGE));
            if (result === 'failed') return ended();
            // Renewed: a session that came back empty (e.g. /user/me refused) has ended too.
            return auth.authSession$.pipe(
              first(),
              switchMap((renewed: IAuthTokenUser | undefined) =>
                renewed?.authIdToken
                  ? next(request.clone({ setHeaders: { Authorization: renewed.authIdToken } }))
                  : ended(),
              ),
            );
          }),
        );
      } else if (currentUser?.authIdToken) {
        return next(request.clone({ setHeaders: { Authorization: currentUser.authIdToken } }));
      }
      return next(request);
    }),
  );
};

export const adminJwtInterceptor: HttpInterceptorFn = (request, next) => {
  const auth = inject(AuthService);
  const platformId = inject(PLATFORM_ID);
  const expired = inject(SESSION_EXPIRED_HANDLER, { optional: true });

  if (isPlatformBrowser(platformId) && request.url.includes(BASE_API_URL)) {
    return attachAuthHeader(request, next, auth, expired);
  }

  return next(request);
};
